import type { Media, Milestone, PaginatedPosts, PostDetail, SiteSetting } from './types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL as string | undefined;
const SITE_ID = import.meta.env.VITE_SITE_ID as string | undefined;

const MAX_PER_PAGE = 100;

export class ApiConfigError extends Error {}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
  }
}

interface CmsErrorBody {
  error: string;
  message: string;
}

function assertConfigured(): { baseUrl: string; siteId: string } {
  if (!API_BASE_URL || !SITE_ID) {
    throw new ApiConfigError(
      'Portfolio is not configured: set VITE_API_BASE_URL and VITE_SITE_ID (see .env.example).',
    );
  }
  return { baseUrl: API_BASE_URL, siteId: SITE_ID };
}

async function request<T>(path: string): Promise<T> {
  const { baseUrl, siteId } = assertConfigured();
  const res = await fetch(`${baseUrl}/sites/${siteId}${path}`);
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as CmsErrorBody | null;
    throw new ApiRequestError(body?.message ?? res.statusText, res.status);
  }
  return res.json() as Promise<T>;
}

async function fetchAllPublishedPosts(): Promise<PaginatedPosts['data']> {
  const posts: PaginatedPosts['data'] = [];
  let page = 1;
  for (;;) {
    const result = await request<PaginatedPosts>(
      `/posts?status=PUBLISHED&page=${page}&perPage=${MAX_PER_PAGE}`,
    );
    posts.push(...result.data);
    if (page >= result.pagination.totalPages) break;
    page += 1;
  }
  return posts;
}

async function fetchMediaMap(): Promise<Map<string, Media>> {
  const media = await request<Media[]>('/media');
  return new Map(media.map((item) => [item.id, item]));
}

/** All published milestones for the timeline, oldest first, with featured images resolved. */
export async function listMilestones(): Promise<Milestone[]> {
  const [posts, mediaMap] = await Promise.all([fetchAllPublishedPosts(), fetchMediaMap()]);
  return posts
    .map((post) => ({
      ...post,
      featuredImage: post.featuredImageId ? (mediaMap.get(post.featuredImageId) ?? null) : null,
    }))
    .sort((a, b) => {
      // Order by when things happened, not when they were written up. Posts
      // with no `occurredAt` (older ones, or an API that predates the field)
      // fall back to `publishedAt` so they still land somewhere sensible.
      const aTime = Date.parse(a.occurredAt ?? a.publishedAt ?? '') || 0;
      const bTime = Date.parse(b.occurredAt ?? b.publishedAt ?? '') || 0;
      return aTime - bTime;
    });
}

export async function getMilestoneDetail(id: string): Promise<PostDetail> {
  return request<PostDetail>(`/posts/${id}`);
}

/**
 * Site-level copy (splash command, summary, skills). Returns a plain map so
 * callers can `settings['splash.command'] ?? fallback`.
 *
 * Never throws: the settings endpoint is new, so an older deployed API 404s
 * here. A missing setting must fall back to a default, not blank the page.
 */
export async function fetchSettings(): Promise<Record<string, string>> {
  try {
    const rows = await request<SiteSetting[]>('/settings');
    return Object.fromEntries(rows.map((row) => [row.key, row.value]));
  } catch {
    return {};
  }
}
